/**
 * AEWebSocket 主线程 Proxy
 * 业务侧调用方式与原 AEWebSocket 完全一致，内部把所有命令转发给 Worker
 * onmessage 解码 + throttle 在 Worker 进行，主线程只接收聚合后的回调数据
 */
import type { IAEWebSocket, IOptions, ISubscribeParams, IUnSubscribeParams, subCallback } from '../types/index'
import type { MainToWorkerMessage, WorkerToMainMessage } from '../types/protocol'
import { WSBaseThrottleWaitEnum, WSThrottleTypeEnum } from '../constants/index'

let _subIdCounter = 0

class AEWebSocketProxy implements IAEWebSocket {
  private worker: Worker

  private options: IOptions = { wsUrl: '', name: '' }

  /** subId → 业务 callback */
  private subIdToCallback = new Map<string, subCallback>()

  /** 业务 callback → subId（unsubscribe 时反查） */
  private callbackToSubId = new WeakMap<subCallback, string>()

  /** 同一 callback 被订阅多次（少见但需兼容），保存 subId 列表 */
  private callbackSubIds = new WeakMap<subCallback, string[]>()

  /** 是否已 setOptions 初始化过 */
  private initialized = false

  /** subId → throttleType，决定消费端合并策略 */
  private subIdToThrottle = new Map<string, WSThrottleTypeEnum>()

  /** 待下发缓冲：cover 存最新一帧，increment 存累积数组 */
  private pending = new Map<string, unknown>()

  /** rAF flush 句柄，0 表示未排程 */
  private rafId = 0

  public onStateChange: ((state: number) => void) | null = null

  constructor(worker: Worker) {
    this.worker = worker
    this.worker.addEventListener('message', this.handleWorkerMessage)
    // 后台标签页 rAF 会暂停，用低频 interval 兜底，保证后台也能合并下发
    setInterval(() => {
      if (this.pending.size > 0) this.flush()
    }, 1000)
  }

  private handleWorkerMessage = (e: { data: WorkerToMainMessage }): void => {
    const msg = e.data
    switch (msg.type) {
      case 'state':
        this.onStateChange?.(msg.state)
        break
      case 'success':
        this.options.success?.()
        break
      case 'push': {
        // Worker 已做背压+合并，收到的是一批已合并数据；按 throttleType 并入 pending
        msg.batch.forEach(({ subId, data }) => {
          if (this.subIdToThrottle.get(subId) === WSThrottleTypeEnum.increment) {
            const prev = (this.pending.get(subId) as unknown[]) || []
            this.pending.set(subId, prev.concat(data))
          } else {
            this.pending.set(subId, data)
          }
        })
        this.scheduleFlush()
        break
      }
      default:
        break
    }
  }

  private post(msg: MainToWorkerMessage): void {
    this.worker.postMessage(msg)
  }

  /** 排程一次 flush；rAF 每帧最多触发一次，天然把一帧内积压的多条 push 合并 */
  private scheduleFlush(): void {
    if (this.rafId) return
    this.rafId = requestAnimationFrame(this.flush)
  }

  /** 把合并后的数据下发给业务回调，并回传 'ready' 通知 Worker 放行下一批（背压闭环） */
  private flush = (): void => {
    // 可能由 interval 兜底触发，取消悬挂的 rAF，避免多余空 flush
    if (this.rafId) {
      cancelAnimationFrame(this.rafId)
      this.rafId = 0
    }
    if (this.pending.size === 0) return
    const entries = Array.from(this.pending.entries())
    this.pending.clear()
    try {
      entries.forEach(([subId, data]) => {
        // 单个业务回调抛错只影响自己，不能中断整批下发
        try {
          this.subIdToCallback.get(subId)?.(data)
        } catch (err) {
          console.error('[ws] flush callback error', subId, err)
        }
      })
    } finally {
      // 无论回调发生什么异常，ready 必须回传，否则 Worker 背压永久卡死、整条行情冻结
      this.post({ type: 'ready' })
    }
  }

  public setOptions(options: IOptions): void {
    this.options = { ...this.options, ...options }
    // 只在第一次 setOptions 时初始化 Worker；后续 setOptions 调用只更新主线程侧的 options
    if (!this.initialized) {
      this.post({
        type: 'init',
        wsName: options.name || '',
        wsUrl: options.wsUrl,
        debug: !!options.debug,
      })
      this.initialized = true
    }
    // 同步当前 token 到 Worker
    const token = options.getToken?.() || ''
    if (token) {
      this.post({ type: 'setToken', token })
    }
  }

  public connect(): Promise<void> {
    this.post({ type: 'connect' })
    return Promise.resolve()
  }

  public close(): void {
    this.post({ type: 'close' })
  }

  public reconnect(): void {
    this.post({ type: 'reconnect' })
  }

  public subscribe(params: ISubscribeParams): void {
    if (!params.subs?.events?.length || !params.callback) return
    // eslint-disable-next-line no-plusplus
    const subId = `s${++_subIdCounter}`
    const throttleType = params.throttleType ?? WSThrottleTypeEnum.none
    this.subIdToCallback.set(subId, params.callback)
    this.subIdToThrottle.set(subId, throttleType)
    this.callbackToSubId.set(params.callback, subId)
    const list = this.callbackSubIds.get(params.callback) || []
    list.push(subId)
    this.callbackSubIds.set(params.callback, list)
    this.post({
      type: 'subscribe',
      subId,
      events: params.subs.events,
      throttleType,
      throttleTime: params.throttleTime ?? WSBaseThrottleWaitEnum.none,
    })
  }

  public unsubscribe(params: IUnSubscribeParams): void {
    const callback = params.callback as subCallback | undefined
    if (!callback) return
    // 兼容 params.subs 多种形态（IWsSubs / array / string）
    const events = this.extractEvents(params.subs)
    if (!events.length) return
    // 取该 callback 最早的 subId 退订（FIFO）
    const list = this.callbackSubIds.get(callback)
    const subId = list?.shift()
    if (!subId) return
    if (list && list.length === 0) {
      this.callbackSubIds.delete(callback)
      this.callbackToSubId.delete(callback)
    }
    this.subIdToCallback.delete(subId)
    this.subIdToThrottle.delete(subId)
    this.pending.delete(subId)
    this.post({ type: 'unsubscribe', subId, events })
  }

  public reSubscribe(topicPrefix: string): void {
    this.post({ type: 'reSubscribe', topicPrefix })
  }

  // 以下方法保留 API 兼容（业务调用过但 core 内部已是 noop）
  public login(): void {}

  public logout(): void {}

  public send(): void {
    // 业务侧不直接调 send；订阅/取消订阅通过 subscribe/unsubscribe 路径
  }

  public onAddWsDelayTimeChange(): void {
    // 延迟统计暂不在 Worker 改造范围
  }

  public onDeleteWsDelayTimeChange(): void {
    // 同上
  }

  public onClearWsDelayTimeChange(): void {
    // 同上
  }

  private extractEvents(subs: unknown): string[] {
    if (!subs) return []
    if (typeof subs === 'string') return [subs]
    if (Array.isArray(subs)) return subs.filter((x): x is string => typeof x === 'string')
    if (typeof subs === 'object' && 'events' in (subs as Record<string, unknown>)) {
      const events = (subs as { events?: unknown }).events
      return Array.isArray(events) ? events.filter((x): x is string => typeof x === 'string') : []
    }
    return []
  }
}

export default AEWebSocketProxy
